import React, { useState, useRef } from 'react';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import Box from '@mui/material/Box';
import closeIcon from "../../assets/icons/Close Square.svg";
import PlayButton from "../../assets/icons/VideoPlaybutton.svg";
import ExpandIcon from "../../assets/icons/Expand.svg";
import PlayIcon from "../../assets/icons/Play.svg";

interface VideoModalProps {
  title: string;
  onClose: () => void;
  videoUrl: string; // Using image URL for now
}

const VideoPreviewModal: React.FC<VideoModalProps> = ({
  title,
  onClose,
  videoUrl
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const duration = 120; // mock duration in seconds (2 mins)

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const handleExpand = () => {
    const elem = containerRef.current;
    if (!elem) return;

    if (!document.fullscreenElement) {
      elem.requestFullscreen?.().catch((err) => {
        console.error("Failed to enter fullscreen:", err);
      });
    } else {
      document.exitFullscreen?.();
    }
  };

  const handleSliderChange = (e: Event, value: number | number[]) => {
    const time = Array.isArray(value) ? value[0] : value;
    setCurrentTime(time);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, '0');
    const secs = Math.floor(seconds % 60)
      .toString()
      .padStart(2, '0');
    return `-${mins}:${secs}`;
  };

  return (
    <Modal open={true} onClose={onClose}>
      <Paper
        elevation={3}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'calc(100vw - 64px)',
          maxWidth: 1120,
          height: 'calc(100vh - 64px)',
          maxHeight: 783,
          gap: '32px',
          borderRadius: '16px',
          padding: '32px',
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
          <IconButton onClick={onClose}>
            <img src={closeIcon} width={24} height={24} alt="close" />
          </IconButton>
        </Box>

        {/* Video/Image Container */}
        <Box
          ref={containerRef}
          sx={{
            position: 'relative',
            width: '100%',
            flex: 1,
            background: '#eef4fc',
            borderRadius: '12px',
            overflow: 'hidden',
          }}
        >
          <img
            src={videoUrl}
            alt="video thumbnail"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />

          {/* Play Button */}
          <IconButton
            onClick={togglePlay}
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              background: 'rgba(0, 0, 0, 0.4)',
              borderRadius: '50%',
            }}
          >
            <img src={PlayButton} width={48} height={48} alt="play" />
          </IconButton>

          {/* Expand Button */}
          <IconButton
            onClick={handleExpand}
            sx={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              width: '32px',
              height: '32px',
              background: '#ffffff',
              borderRadius: '4px',
              border: '1px solid #f0eff8',
            }}
          >
            <img src={ExpandIcon} width={24} height={24} alt="fullscreen" />
          </IconButton>

          {/* Controls (Slider + time) */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              px: 2,
              py: 1,
              background: 'rgba(0, 0, 0, 0.4)',
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img src={PlayIcon} width={20} height={20} alt="play" />
              <Slider
                min={0}
                max={duration}
                value={currentTime}
                onChange={handleSliderChange}
                sx={{
                  flex: 1,
                  color: '#5547dd',
                  '& .MuiSlider-track': {
                    backgroundColor: '#c83333',
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: '#e0e0e0',
                  },
                  '& .MuiSlider-thumb': {
                    backgroundColor: '#5547dd',
                    border: '2px solid white',
                  },
                }}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Typography sx={{ fontSize: '14px', color: '#fff' }}>
                {formatTime(duration - currentTime)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default VideoPreviewModal;

